import { useCallback, useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage } from "@/utils/safeStorage";

// Global cache: lang:text → translated text
const translationCache = new Map<string, string>();

// Load persisted cache on module init
try {
  const stored = safeLocalStorage.getItem("gb-translations");
  if (stored) {
    const parsed = JSON.parse(stored);
    for (const [k, v] of Object.entries(parsed)) {
      translationCache.set(k, v as string);
    }
  }
} catch {}

// Persist cache periodically (debounced)
let persistTimer: ReturnType<typeof setTimeout> | null = null;
function persistCache() {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      const obj: Record<string, string> = {};
      // Limit cache size to 2000 entries
      let count = 0;
      for (const [k, v] of translationCache.entries()) {
        if (count++ > 2000) break;
        obj[k] = v;
      }
      safeLocalStorage.setItem("gb-translations", JSON.stringify(obj));
    } catch {}
  }, 2000);
}

// Batching: collect texts for 100ms then send one request
type BatchEntry = { text: string; resolve: (val: string) => void };
let batchQueue: BatchEntry[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let currentBatchLang = "";

async function flushBatch() {
  const entries = batchQueue.splice(0);
  const lang = currentBatchLang;
  if (entries.length === 0) return;

  const texts = entries.map((e) => e.text);
  try {
    const { data, error } = await supabase.functions.invoke("batch-translate", {
      body: { texts, targetLanguage: lang },
    });

    if (error || !data?.translations) {
      // Fallback: resolve with originals
      entries.forEach((e) => e.resolve(e.text));
      return;
    }

    const translations: string[] = data.translations;
    entries.forEach((e, i) => {
      const translated = translations[i] || e.text;
      const cacheKey = `${lang}:${e.text}`;
      translationCache.set(cacheKey, translated);
      e.resolve(translated);
    });
    persistCache();
  } catch {
    entries.forEach((e) => e.resolve(e.text));
  }
}

function queueTranslation(text: string, lang: string): Promise<string> {
  return new Promise((resolve) => {
    // If language changed mid-batch, flush old batch
    if (currentBatchLang && currentBatchLang !== lang && batchQueue.length > 0) {
      if (batchTimer) clearTimeout(batchTimer);
      flushBatch();
    }
    currentBatchLang = lang;
    batchQueue.push({ text, resolve });

    if (batchTimer) clearTimeout(batchTimer);
    // Flush when batch is full
    if (batchQueue.length >= 50) {
      flushBatch();
    } else {
      batchTimer = setTimeout(flushBatch, 100);
    }
  });
}

/**
 * Translates a single string. Returns original text immediately,
 * then updates when translation arrives.
 */
export function useTranslation(text: string): string {
  const { language, languageName, isEnglish } = useLanguage();
  const [translated, setTranslated] = useState(text);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isEnglish || !text) {
      setTranslated(text);
      return;
    }

    const cacheKey = `${languageName}:${text}`;
    const cached = translationCache.get(cacheKey);
    if (cached) {
      setTranslated(cached);
      return;
    }

    // Show original while loading
    setTranslated(text);

    queueTranslation(text, languageName).then((result) => {
      if (mountedRef.current) {
        setTranslated(result);
      }
    });
  }, [text, language, languageName, isEnglish]);

  return translated;
}
